import { Button, Flex, Text } from '@servicetitan/anvil2';
import { ErrorInfo } from '@servicetitan/financing-api-client';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { HtmlContent } from '../components';
import { errorIcon, message, serverErrorDetails } from './ServerErrorState.module.scss';
import { extractFinancingIdentifiersFromResponse } from '../utils/extractFinancingIdentifiersFromResponse';
import { trackEvent } from '../utils/fullstory-tracker';

/**
 * Component to display server errors
 */
interface ServerErrorStateProps {
    errorDetails: ServerErrorDetails;
    onRetry?: () => void;
    onBack?: () => void;
    backLabel?: string;
}

export interface ServerErrorResponse {
    data?: string | ErrorInfo | ErrorInfo[];
    headers: { [key: string]: string };
}

export type ServerError = AxiosError<ServerErrorResponse> | string | Error;

export interface ServerErrorDetails {
    error: ServerError;
    actionCausingError: string | null;
}

export const ServerErrorState = ({
    errorDetails,
    onRetry,
    onBack,
    backLabel,
}: ServerErrorStateProps) => {
    const { error, actionCausingError } = errorDetails;
    const { message: errorMessage, correlationId } = extractMessageAndCorrelationId(error);

    // Track error in FullStory
    useEffect(() => {
        // Only track axios errors (not string or generic Error objects)
        if (typeof error === 'string' || !(error instanceof Error && 'config' in error)) {
            return;
        }

        const axiosError = error as AxiosError;

        const loanIdentifiers = extractFinancingIdentifiersFromResponse(axiosError.response?.data);

        // Build event properties
        const eventProperties: Record<string, any> = {
            errorMessage,
            actionCausingError,
            sessionId: axiosError.config?.headers?.['X-ST-SessionId'],
            correlationId,
            method: axiosError.config?.method?.toUpperCase(),
            url: axiosError.config?.url,
            providerName: axiosError.config?.headers?.['X-ST-FIN-Provider'],
            source: axiosError.config?.headers?.['X-ST-FIN-Origin'],
            statusCode: axiosError.response?.status,
            statusText: axiosError.response?.statusText,
            ...loanIdentifiers,
        };

        // Send FullStory event
        trackEvent('FIN-API-Error', eventProperties);
    }, [error, errorMessage, correlationId, actionCausingError]);

    return (
        <Flex
            direction="column"
            alignItems="center"
            justifyContent="start"
            className={serverErrorDetails}
        >
            <div className={errorIcon}>
                <svg
                    width="64"
                    height="64"
                    viewBox="0 0 64 64"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M32 0C14.3269 0 0 14.3269 0 32C0 49.6731 14.3269 64 32 64C49.6731 64 64 49.6731 64 32C64 14.3269 49.6731 0 32 0ZM32 9.14266C33.4077 9.14266 34.5585 10.2935 34.5585 11.7012V36.1156C34.5585 37.5233 33.4077 38.6741 32 38.6741C30.5923 38.6741 29.4415 37.5233 29.4415 36.1156V11.7012C29.4415 10.2935 30.5923 9.14266 32 9.14266ZM32 54.8579C29.1846 54.8579 26.9041 52.5774 26.9041 49.7621C26.9041 46.9467 29.1846 44.6662 32 44.6662C34.8154 44.6662 37.0959 46.9467 37.0959 49.7621C37.0959 52.5774 34.8154 54.8579 32 54.8579Z"
                        fill="#D73636"
                    />
                </svg>
            </div>
            <div className={message}>
                <h3>{`There was a problem ${actionCausingError}`}</h3>
                <div style={{ margin: '0px' }}>
                    <HtmlContent
                        content={errorMessage}
                        allowedTags={['a', 'b', 'strong', 'i', 'em', 'u', 'br', 'p']}
                        allowedAttributes={['href', 'target', 'rel']}
                    />
                </div>
                {onRetry && <Text>Please try again.</Text>}
                {correlationId && <Text>Error correlation ID: {correlationId}</Text>}
                {onRetry && (
                    <Button appearance="primary" onClick={onRetry}>
                        Try Again
                    </Button>
                )}
                {onBack && <Button onClick={onBack}>{backLabel ?? 'Back'}</Button>}
            </div>
        </Flex>
    );
};

export function extractMessageAndCorrelationId(error: ServerError): {
    message: string;
    correlationId: string | null;
} {
    let message = '';
    let correlationId: string | null = null;

    if (typeof error !== 'string' && ('response' in error || 'config' in error)) {
        const response = error.response ?? error.config;

        if (!response) {
            return { message: '', correlationId: null };
        }

        // Check for gateway/proxy errors from nginx (no correlation ID for these)
        if ('status' in response && [502, 503, 504].includes(response.status)) {
            const errorMessages: { [key: number]: string } = {
                502: 'The server is temporarily unavailable.',
                503: 'The service is temporarily unavailable.',
                504: 'The request took too long to process.',
            };
            return {
                message:
                    errorMessages[response.status] || 'A server error occurred. Please try again.',
                correlationId: null,
            };
        }

        if (response.headers && typeof response.headers.get === 'function') {
            // error.config is AxiosRequestConfig, which has the request headers
            correlationId = (response.headers.get('x-st-correlationid') ??
                error.config?.headers.get('x-st-correlationid')) as string;
        }

        if (!response.data) {
            return { message: '', correlationId };
        }

        let data: ErrorInfo | ErrorInfo[] | string;

        if (typeof response.data === 'string' || response.data instanceof String) {
            const strData = response.data.toString();
            try {
                data = JSON.parse(strData);
            } catch {
                data = strData;
            }
        } else {
            data = response.data;
        }

        if (typeof data === 'string') {
            message = data;
        } else if (Array.isArray(data) && data.length > 0 && data[0].description) {
            message = data
                .map(d =>
                    d.invalidParameter
                        ? `Error in: ${d.invalidParameter}; error: ${d.description}`
                        : d.description,
                )
                .join(', ');
        } else if ((data as ErrorInfo)?.description) {
            message = (data as ErrorInfo).description ?? '';
        }
    } else if (typeof error === 'string') {
        message = error;
    } else if (error instanceof String) {
        message = error.toString();
    } else if (error instanceof Error) {
        message = error.message;
    }

    return { message, correlationId };
}
